/**
 * Single declared vocabulary for the four backend `SaleOrderStage` values plus the
 * view-only `Draft` state, and the single declared set of conditions that block a
 * stage move.
 *
 * Backend truth: `SaleOrderStage` enum
 * (`crm-database/src/MojitoCRM.DataBaseAPI.Models/Enums/SaleOrderStage.cs`) —
 * `Budget = 1, BudgetSent = 2, SaleOrder = 3, Cancelled = 4`. Resolution MUST key on
 * `value`/`enumValue`, never on array position or column order — see
 * `quote-stage-lifecycle` spec, "Stage resolution is keyed on stage value".
 *
 * This is the only declaration of this vocabulary in `crm-web`. Do not add a parallel
 * copy — delegate to `BUDGET_STAGES` / `findBudgetStageBlock` instead.
 */

export type BudgetStageValue = 'Budget' | 'BudgetSent' | 'SaleOrder' | 'Cancelled';

/**
 * The verb that persists a move into this stage. `SaleOrder` is owned by `confirm`,
 * `Cancelled` by `cancel`; every other transition goes through the dedicated
 * `changestage` route.
 */
export type BudgetStageRoute = 'changestage' | 'confirm' | 'cancel';

export interface BudgetStage {
  readonly value: BudgetStageValue;
  readonly enumValue: 1 | 2 | 3 | 4;
  readonly name: string;
  readonly icon: string;
  readonly color: string;
  readonly route: BudgetStageRoute;
}

export interface DraftBudgetStage {
  readonly value: 'Draft';
  readonly enumValue: null;
  readonly name: string;
  readonly icon: string;
  readonly color: string;
  readonly route: null;
}

export const BUDGET_STAGES: readonly BudgetStage[] = [
  { value: 'Budget', enumValue: 1, name: 'Presupuesto', icon: 'paid', color: '#0072F5', route: 'changestage' },
  { value: 'BudgetSent', enumValue: 2, name: 'Presupuesto enviado', icon: 'send', color: '#8E4EC6', route: 'changestage' },
  { value: 'SaleOrder', enumValue: 3, name: 'Pedido de venta', icon: 'order_approve', color: '#6CDA75', route: 'confirm' },
  { value: 'Cancelled', enumValue: 4, name: 'Cancelado', icon: 'cancel', color: '#CB2A2F', route: 'cancel' },
] as const;

/**
 * `Draft` has no backend enum value; it is a view-only state, prepended only when
 * `isActive === false`. `route: null` is what makes it non-selectable as a transition
 * target — one field, not a special case elsewhere.
 */
export const DRAFT_BUDGET_STAGE: DraftBudgetStage = {
  value: 'Draft',
  enumValue: null,
  name: 'Borrador',
  icon: 'edit_note',
  color: '#60BFFF',
  route: null,
};

/** Resolves a stage by its backend value. Never resolve by index/order. */
export function getBudgetStageByValue(
  value: string | null | undefined,
): BudgetStage | undefined {
  return BUDGET_STAGES.find((stage) => stage.value === value);
}

export interface BudgetStageBlockSubject {
  readonly stage?: string | null;
  readonly dateCancelled?: string | null;
  readonly dateConfirm?: string | null;
}

export interface BudgetStageBlock {
  readonly id: string;
  readonly reason: string;
  readonly applies: (quote: unknown) => boolean;
}

/**
 * Q1 RESOLVED 2026-08-31 (Branch B) — adoption does NOT block; only cancellation and
 * confirmation do. The `adopted` entry stays commented. Do not uncomment it without
 * re-opening Q1 in `quote-stage-lifecycle` spec.
 *
 * Cancellation carve-out (F-2): callers MUST NOT evaluate this set when the target is
 * cancellation — a confirmed quote may still be cancelled. `findBudgetStageBlock` is
 * therefore never called on the cancellation path; see call sites.
 */
export const BUDGET_STAGE_BLOCKS: readonly BudgetStageBlock[] = [
  {
    id: 'cancelled',
    reason: 'El presupuesto está cancelado.',
    applies: (quote: unknown) => {
      const q = quote as BudgetStageBlockSubject | null | undefined;
      return !!q?.dateCancelled || q?.stage === 'Cancelled';
    },
  },
  {
    id: 'confirmed',
    reason: 'El presupuesto ya está confirmado.',
    applies: (quote: unknown) => {
      const q = quote as BudgetStageBlockSubject | null | undefined;
      return !!q?.dateConfirm;
    },
  },
  // Q1 BRANCH A — adoption blocks: uncomment this single line (and its
  // `SaleOrderStageRules.cs` twin in crm-core).
  // {
  //   id: 'adopted',
  //   reason: 'El presupuesto está adoptado.',
  //   applies: (quote: unknown) => (quote as { budgetAdopted?: boolean } | null | undefined)?.budgetAdopted === true,
  // },
];

/**
 * Single enforcement point for whether a quote may change stage. Run on every
 * transition path — except when the target is cancellation (F-2 carve-out).
 */
export function findBudgetStageBlock(quote: unknown): BudgetStageBlock | null {
  return BUDGET_STAGE_BLOCKS.find((block) => block.applies(quote)) ?? null;
}
